import {
  Injectable
} from '@angular/core';

import {
  HttpClient,
  HttpHeaders,
} from '@angular/common/http';
import {
  Application, Document, ODKResponse, ReasonByProductType, ReturnReason} from '../../models/ApplicationModel';

import {
  Observable,
  of
} from 'rxjs';

import { environment } from '../../../environments/environment';
import * as Variables from '../../../environments/app.variables';
import { DataTableResponse } from '../../models/DatatableResponse';
import {AccountService} from '../../services/account.service';
import * as Constants from '../../models/Constants';
import {ApplicationCategory, FacilityType, ProductType, FoodManufDoc, ApiResponse} from '../../models/CommonModels';
import {DraftApplicationModel} from '../../models/ApplicationDetail';
import { RenewalFee } from '../../models/RenewalFee';
import { changeTypeRefData } from '../../models/CommonModels';
import { ErisLoginService } from '../../services/sso/eris-login.service';
import { SubDataVariables } from '../../../environments/app.variables';
import { DocumentRef } from '../../models/DocumentRef';
import { PaymentRequest, PaymentConfirmation } from '../../models/ApplicationModel';

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  })
};

@Injectable({
  providedIn: 'root'
})

export class ApplicationService {

  private applicationUrl = environment.baseUrl + '/Application';
  private tempCOCApplicationUrl = environment.baseUrl + '/Application/Covid19TempCOC';
  private LegacyDataUpdateUrl = environment.baseUrl + '/Application/LegacyDataUpdate';
  private docGenerateURL = environment.baseUrl + '/DocumentGenerator';
  private previewCertificateURL = environment.baseUrl + '/Attachment/Certificate/';
  private previewNotificationLetterURL = environment.baseUrl + '/Attachment/NotificationLetter/';
  private documentRefUrl = environment.baseUrl + '/DocumentRef';
  private attachmentUrl = environment.baseUrl + '/Attachment';
  private reciptNumberUrl = this.applicationUrl + '/AddPaymentReciept';
  private npaUrl = this.applicationUrl + '/NonPrimaryAssignees/';
  private changeInspInfoUrl = this.applicationUrl + '/changeInspectionInfo';
  private draftApplications = environment.baseUrl + '/ApplicationDraft';
  private checklistScreen = environment.baseUrl + '/CheckListInst/ScreenChecklists';
  private calculateFeeForRenewal = environment.baseUrl + '/FeeInfo/calculateRenewalFee/';
  private calculateFeeForChange = environment.baseUrl + '/FeeInfo/calculateChangeFee/';
  private FIRItemUrl = environment.baseUrl + '/FIRItem/FIRItemsByApplication';
  private TINUrl = environment.baseUrl + '/Facility/GetFacilityInfo/';
  private returnToApplicantUrl = environment.baseUrl + '/ReasonModule/BySubmoduleType/';
  private feeInfoUrl = environment.baseUrl + '/FeeInfo';
  private telebirrUrl = environment.baseUrl + '/Telebirr/CreateOrder';
  private telebirrCallbackUrl = environment.baseUrl + '/Telebirr/Callback';
  private telebirrBillLookupUrl = environment.baseUrl + '/Telebirr/ApplicationIdByBill';
  private confirmPaymentUrl = environment.baseUrl + '/Payment/ConfirmPayment';

  private paymentReferralURL = Variables.DataVariables.PAYMENT_REFERRAL_URL + '/';
  private unassignedAppFilter = false;
  private currentlyAssignedToMeAppFilter = false;
  private approvedByMeAppFilter = false;
  private _applicationCategory = '';
  private _applicationStatus = '';

  constructor(private http: HttpClient, private accountService: AccountService, private erisLoginService: ErisLoginService) {
    httpOptions.headers = httpOptions.headers.set('Authorization', 'Bearer ' + this.accountService.getTokenData());
  }

  /** POST: add a new hero to the server */
  addApplication(application: any, tempCOC: Boolean): Observable < any > {
    if (tempCOC) {
      return this.http.post < any > (this.tempCOCApplicationUrl, application, httpOptions);
    } else {
      return this.http.post < any > (this.applicationUrl, application, httpOptions);
    }
  }

  editApplication(application: Application, tempCOC: Boolean): Observable < Application > {
    if (tempCOC) {
      return this.http.put < Application > (this.tempCOCApplicationUrl, application, httpOptions);
    } else {
      return this.http.put < Application > (this.applicationUrl, application, httpOptions);
    }
  }

  updateApplication(application: Application): Observable < Application > {
    return this.http.post < Application > (this.LegacyDataUpdateUrl, application, httpOptions);
  }

  getDataTableOptions(applications) {
    const that = this;

    const currentUserRoleCodes = this.accountService.getUserRoleCodes();
    // i used the column unassigned_filter to send the filter data to the server (need to find a better way)
    let cols = []
    if (this.isUserApplicant(currentUserRoleCodes)) { // hide assigned to header on application list
      cols = [{ data: 'id' },{ data: 'applicationStatusCode' }, {data: 'facilityName'}, {data: 'facilityTypeName'}, {data: 'productType'},
        {data: 'applicationCategoryName'},  { data: 'modifiedDate' },{data: 'assignedUser', visible:false} ];
    } else {
      cols = [{data: 'placeholder', orderable: false}, { data: 'id' }, { data: 'applicationStatusCode' }, {data: 'facilityTypeName'},
        {data: 'productType'},  { data: 'modifiedDate' }, {data: 'assignedUser'}, {data: 'dueDate'}, {data: 'ticketNo'} ];
    }

    let options = {
      pagingType: 'full_numbers',
      pageLength: 10,
      serverSide: true,
      processing: true,
      order : this.isUserApplicant(currentUserRoleCodes) ? [[6, 'desc']] : [[5, 'desc']],
      language : {
        processing : '<div class="sk-spinner sk-spinner-three-bounce"> <div class="sk-bounce1"></div> <div class="sk-bounce2"></div> <div class="sk-bounce3"></div> </div>'
      },
      ajax: (dataTablesParameters: any, callback) => {
        // Build Authorization per request — capturing it when options are created often
        // baked in a null token after refresh ("worked then Application/List 401").
        let headers = new HttpHeaders()
          .set('Content-Type', 'application/x-www-form-urlencoded')
          .set('Authorization', 'Bearer ' + that.accountService.getTokenData());

        dataTablesParameters.unassignedFilter = this.unassignedAppFilter;
        dataTablesParameters.assignedToMeFilter = this.currentlyAssignedToMeAppFilter;
        dataTablesParameters.approvedRejectedByMeFilter = this.approvedByMeAppFilter;
        let url = '/Application/List';
        if (this._applicationCategory === Constants.APPLICATION_TYPE_CODE_NEW ||
          this._applicationCategory === Constants.APPLICATION_TYPE_CODE_RENEWAL ||
          this._applicationCategory === Constants.APPLICATION_TYPE_CODE_CHANGE ||
          this._applicationCategory === Constants.APPLICATION_TYPE_CODE_REPLACEMENT ||
          this._applicationCategory === Constants.APPLICATION_TYPE_CODE_ADD_WAREHOUSE ||
          this._applicationCategory === Constants.APPLICATION_TYPE_CODE_TEMP_COC ||
          this._applicationCategory === Constants.APPLICATION_TYPE_CODE_RETURN_COC ||
          this._applicationCategory === Constants.APPLICATION_TYPE_CODE_TEMP_ADD_WAREHOUSE ||
          this._applicationCategory === Constants.APPLICATION_TYPE_CODE_TEMP_CHANGE ||
          this._applicationCategory === Constants.APPLICATION_TYPE_CODE_TEMP_RETURN_COC) {
          dataTablesParameters.applicationCategoryCode = this._applicationCategory;
          url = '/Application/ByCategory';
        }

        if (this._applicationStatus === Constants.DELETED || this._applicationStatus === Constants.QUARANTINED){
          dataTablesParameters.applicationStatusCode = this._applicationStatus;
          url = '/Application/ByStatus';
        }

        that.http.post<DataTableResponse>(environment.baseUrl + url, $.param(dataTablesParameters), {headers: headers})
        	.subscribe(
            resp => {
              applications.splice(0, applications.length);
              // can't use users=resp.data because we want to pass by reference
              for (let app of resp.data){
                applications.push(app);
              }
	            callback({
	              recordsTotal: resp.recordsTotal,
	              recordsFiltered: resp.recordsFiltered,
	              data: []
              });
            },
            error => {
	            callback({
	              recordsTotal: 0,
	              recordsFiltered: 0,
	              data: []
              });
            }
          );
      },
      columns: cols
    }

    return options;
  }

  public isUserApplicant(currentUserRoleCodes) {
    if(currentUserRoleCodes.length === 0){
      // means the user data is not loaded yet, this happens the first time login
      // the datatable can't be configured with an event listener(using Behaviour Subject)
      // so for now we use the user_info from the sso
      // TODO: use a datatable library that can be configured better
      let profile = this.erisLoginService.getProfile();
      let role = profile["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"];
      if(typeof(role) === 'string' && role === 'IP Applicant'){
        return true;
      } else {
        return false;
      }
    }
    return currentUserRoleCodes.indexOf(Constants.APPLICANT) !== -1;
  }

  getReturnApplicationReasons(productTypeName){
    return this.http.get<ReasonByProductType []>(this.returnToApplicantUrl + Constants.ILISCENCE_MODULE_CODE + '/' + productTypeName, httpOptions);
  }

  getDraftApplications(userId) {
    return this.http.get<DraftApplicationModel[]>(this.draftApplications + '/GetByUser/' + userId, httpOptions);
  }

  saveDraftApplication(data) {
    return this.http.post<any>(this.draftApplications + '/SaveAsDraft', data, httpOptions);
  }

  getDraftApplication(applicationId) {
    return this.http.get<DraftApplicationModel>(this.draftApplications + '/' + applicationId, httpOptions);
  }

  deleteDraftApplication(draftAppId) {
    return this.http.delete<any>(this.draftApplications + '/' + draftAppId, httpOptions);
  }

  assignApplicationToUser(data) {
    return this.http.post<any>(environment.baseUrl + '/Application/AssignAppToUser', data, httpOptions);
  }

  addPaymentRecieptNoToApplication(data: any) {
    return this.http.post<any>(this.reciptNumberUrl, data, httpOptions);
  }

  generateBillNumber(appId: number) : Observable<ApiResponse> {
    return this.http.put<ApiResponse>(this.applicationUrl + '/GenerateBillNumber/' + appId, null);
  }

  changeInspectionInfo(data: any) {
    return this.http.post<any>(this.changeInspInfoUrl, data, httpOptions);
  }

  getDocumentRef(facilityTypeId: number, prodTypeId: number, plc: boolean, focalPerson: boolean , applicationCategory: ApplicationCategory,
                 facilityCategoryId,foodManuf:FoodManufDoc, changeTypeRefIds: Array<number> = null, replacementReasonId= null) {   
    
    if (applicationCategory && ['REN', 'REP', 'RET', 'WHA', 'TWA', 'RTC'].indexOf(applicationCategory.applicationCategoryCode) > -1) {
      return this.http.get<any>(this.documentRefUrl + '/filter/' + facilityTypeId + '/' + prodTypeId + '/' + applicationCategory.id
        + '/' + facilityCategoryId + '/' + replacementReasonId, httpOptions);
    } else if(applicationCategory && applicationCategory.applicationCategoryCode === Constants.APPLICATION_TYPE_CODE_TEMP_COC){
      return this.http.get<any>(this.documentRefUrl + '/tempCOC/' + facilityTypeId + '/' + prodTypeId + '/' + plc + '/' + focalPerson, httpOptions);
    } else if(applicationCategory && (applicationCategory.applicationCategoryCode === Constants.APPLICATION_TYPE_CODE_CHANGE || 
      applicationCategory.applicationCategoryCode === Constants.APPLICATION_TYPE_CODE_TEMP_CHANGE)){
      let data = new changeTypeRefData(changeTypeRefIds, facilityTypeId, prodTypeId, applicationCategory.id);
      return this.http.post<any>(this.documentRefUrl + '/change', data, httpOptions);
    } else {
      if(foodManuf){
        return this.http.get<any>(this.documentRefUrl + '/FoodManufacturer/' + foodManuf.mLevelID + '/' + foodManuf.mRLevelID + '/' + plc + '/' + focalPerson , httpOptions);
      }
      return this.http.get<any>(this.documentRefUrl + '/' + facilityTypeId + '/' + prodTypeId + '/' + plc + '/' + focalPerson, httpOptions);
    }
  }

  getApplication(applicationId) : any {
    return this.http.get(this.applicationUrl + '/' + applicationId, httpOptions);
  }

  changeApplicationStatus(data: any) {
    return this.http.post < any > (this.applicationUrl + '/changeStatus', data, httpOptions);
  }

  generateCertificate(data: any) {
    return this.http.post < any > (this.docGenerateURL, data, httpOptions);
  }

  previewCertificate(appId: any) {
    httpOptions['responseType'] = 'blob';
    return this.http.get < any > (this.previewCertificateURL + appId, httpOptions);
  }

  previewNotificationLetter(appId: any) {
    httpOptions['responseType'] = 'blob';
    return this.http.get < any > (this.previewNotificationLetterURL + appId, httpOptions)
  }

  previewPaymentReferral(appId: any) {
    httpOptions['responseType'] = 'blob';

    return this.http.get < any > (this.paymentReferralURL + appId, httpOptions);
  }

  getApplicationStatus() {
    return this.http.get<Array<any>>(this.applicationUrl + '/getStatuses', httpOptions);
  }

  getApplicationHistory(applicationId: number) {
    return this.http.get<any>(this.applicationUrl + '/AllHistory/' + applicationId, httpOptions);
  }

  set unassignedFilter(filter) {
    this.unassignedAppFilter = filter;
  }
  set approvedByMeFilter(filter) {
    this.approvedByMeAppFilter = filter;
  }
  set currentlyAssignedToMeFilter(filter) {
    this.currentlyAssignedToMeAppFilter = filter;
  }
  set applicationCategory(appCat) {
    this._applicationCategory = appCat;
  }

  set applicationStatus(appStatus){
    this._applicationStatus = appStatus;
  }

  getAttachmentByID(attachmentID) {
    httpOptions['responseType'] = 'blob';
    return this.http.get<any>(this.attachmentUrl + '/Single/' + attachmentID, httpOptions);
  }

  getNonPrimayAssignees(applicationId) {
    return this.http.get<any>(this.npaUrl + applicationId, httpOptions);
  }

  getProfessionalTypesForFacilityAndProduct(facility: FacilityType, product: ProductType, riskLevel, manufacturingLevel) {
    if (facility.name === Constants.MANUFACTURER) {
      if (product.name === Constants.PRODUCT_TYPE_FOOD &&
        ([Constants.MANUFACTURING_RISK_LEVEL_HIGH, Constants.MANUFACTURING_RISK_LEVEL_SPECIAL].indexOf(riskLevel) > -1 ||
          (riskLevel === Constants.MANUFACTURING_RISK_LEVEL_LOW &&
            [Constants.MANUFACTURING_LEVEL_HIGHER, Constants.MANUFACTURING_LEVEL_MEDIUM].indexOf(manufacturingLevel) > -1))) {
        return [Constants.PRODUCTION_MANAGER_CODE, Constants.QUALITY_ASSURANCE_CODE, Constants.HYGIENE_AND_SANITATION_EXPERT_CODE,
          Constants.PHYSICO_CHEMICAL_EXPERT_CODE, Constants.MICROBIOLOGIST_CODE];

      } else if (product.name === Constants.PRODUCT_TYPE_FOOD && 
        [Constants.MANUFACTURING_LEVEL_HIGHER, Constants.MANUFACTURING_LEVEL_MEDIUM].indexOf(manufacturingLevel) > -1) {
        return [Constants.PRODUCTION_MANAGER_CODE, Constants.QUALITY_ASSURANCE_CODE, Constants.HYGIENE_AND_SANITATION_EXPERT_CODE];

      } else if(product.name === Constants.PRODUCT_TYPE_FOOD){
        return [Constants.PRODUCTION_MANAGER_CODE, Constants.QUALITY_ASSURANCE_CODE];

      } else if ([Constants.PRODUCT_TYPE_MEDICINE,Constants.PRODUCT_TYPE_MED_DEVICES].indexOf(product.name) !== -1) {
        return [Constants.TECHNICAL_LEADER_CODE,Constants.PRODUCTION_MANAGER_CODE, Constants.QUALITY_ASSURANCE_CODE,Constants.Quality_CONTROL_CODE];

      }  else {
        return [Constants.TECHNICAL_LEADER_CODE,Constants.PRODUCTION_MANAGER_CODE, Constants.QUALITY_ASSURANCE_CODE];
      }      
    } else {
     if (product.name === Constants.PRODUCT_TYPE_FOOD) {
       return [Constants.TECHNICAL_LEADER_CODE];
     } else {
       return [Constants.TECHNICAL_LEADER_CODE, Constants.STORE_MANAGER_CODE];
     }
    }
  }

  checklistScreenedWithResponse(screenerResponse: any) {
    return this.http.put<any>(this.checklistScreen, screenerResponse, httpOptions);
  }

  calculateFeeForFacilityRenewal(facilityId) {
    return this.http.get<RenewalFee>(this.calculateFeeForRenewal + facilityId, httpOptions);
  }

  calculateFeeForFacilityChange(facilityId, changeTypeRefId, tempCOC) {
    return this.http.get<any>(this.calculateFeeForChange + facilityId + '/' + changeTypeRefId + '/' + (tempCOC ? true : false), httpOptions);
  }

  getRequiredDocumentsForRenewal(facilityTypeId,productTypeId,appCategoryId,facilityCategoryId) {
    return this.http.get<any>(this.documentRefUrl + '/filter/' + facilityTypeId + '/' + productTypeId + '/' + appCategoryId + '/' + facilityCategoryId + '/null', httpOptions);
  }

  getRequiredDocumentsForChange(facilityId, changeTypeRefId, tempCOC) {
    return this.http.get<any>(this.documentRefUrl + '/change/' + facilityId + '/' + changeTypeRefId + '/' + (tempCOC ? true : false), httpOptions);
  }

  getRequiredDocumentsForReplacement(facilityTypeId,productTypeId,appCategoryId,facilityCategoryId,reasonId) {
    return this.http.get<any>(this.documentRefUrl + '/filter/' + facilityTypeId + '/' + productTypeId + '/' + appCategoryId + '/' + facilityCategoryId + '/' + reasonId , httpOptions);
  }

  getRequiredDocumentsForReturn(facilityId,productTypeId,appCategoryId,facilityCategoryId) {
    return this.http.get<any>(this.documentRefUrl + '/filter/' + facilityId + '/' + productTypeId + '/' + appCategoryId + '/' + facilityCategoryId + '/' + null , httpOptions);
  }

  getRequiredDocumentsForWarehouseAddition(facilityId,productTypeId,appCategoryId,facilityCategoryId) {
    return this.http.get<any>(this.documentRefUrl + '/filter/' + facilityId + '/' + productTypeId + '/' + appCategoryId + '/' + facilityCategoryId + '/' + null , httpOptions);
  }

  submitApplicationRenewal(renewal) {
    return this.http.post<any>(this.applicationUrl + '/renew', renewal,  httpOptions);
  }

  submitApplicationReplacement(replacement) {
    return this.http.post<any>(this.applicationUrl + '/replacement', replacement,  httpOptions);
  }

  submitApplicationReturn(returnApp, tempCOC) {
    if(tempCOC){      
      return this.http.post<any>(this.applicationUrl + '/ReturnTempCOC', returnApp,  httpOptions);
    } else {     
      return this.http.post<any>(this.applicationUrl + '/returnCOC', returnApp,  httpOptions);
    }
  }

  submitApplicationChange(change, tempCOC) {
    if(tempCOC){
      return this.http.post<any>(this.applicationUrl + '/TempCOCChange', change,  httpOptions);
    }else{
      return this.http.post<any>(this.applicationUrl + '/change', change,  httpOptions);
    }
  }

  submitApplicationWarehouse(warehouse, tempCOC) {
    if(tempCOC){
      return this.http.post<any>(this.applicationUrl + '/TempCOCAdditionalWarehouse', warehouse,  httpOptions);
    }
    return this.http.post<any>(this.applicationUrl + '/AdditionalWarehouse', warehouse,  httpOptions);
  }

  editApplicationChange(change, tempCOC) {
    if(tempCOC){
      return this.http.put<any>(this.applicationUrl + '/TempCOCChange', change,  httpOptions);
    }else{
      return this.http.put<any>(this.applicationUrl + '/change', change,  httpOptions);
    }
  }

  editRenewalApplication(renewal) {
    return this.http.put<any>(this.applicationUrl + '/renewal', renewal,  httpOptions);
  }

  editReplacementApplication(replacement) {
    return this.http.put<any>(this.applicationUrl + '/replacement', replacement,  httpOptions);
  }

  editWarehouseAdditionApplication(warehouse, tempCOC){
    if(tempCOC){
      return this.http.put<any>(this.applicationUrl + '/TempCOCAdditionalWarehouse', warehouse,  httpOptions);
    }
    return this.http.put<any>(this.applicationUrl + '/AdditionalWarehouse', warehouse,  httpOptions);
  }

  editReturnApplication(returnApp) {
    return this.http.put<any>(this.applicationUrl + '/returnCOC', returnApp,  httpOptions);

  }

  getDocRefById(id){
    return this.http.get<any>(this.documentRefUrl + '/' + id);
  }

  getFIRItem(id){
    return this.http.get<any>(this.FIRItemUrl + '/' + id )
  }

  getFacilityInfoForTinNumber(tinNumber) {
    return this.http.get<any>(this.TINUrl + tinNumber, httpOptions);
  }

  generatePreInspectionQRCode(appId){
    return this.http.get<Document>(this.docGenerateURL + '/GenerateInspectionQRCode/PreInspection/' + appId);
  }

  deleteQRCodeDocument(appId){
    return this.http.delete<boolean>(SubDataVariables.PREINSPECTION_QRCODE_URL + '/' + appId);
  }

  getPreInspectionDocRefs(){
    return this.http.get<DocumentRef[]>(this.documentRefUrl + '/PreInspectionDocuments');
  }

  submitPreInspectionPrerequisiteInfo(data) {
    return this.http.post<boolean>(this.applicationUrl + '/SubmitPreInspectionPrerequisiteInfo', data)
  }

  // regenerate certificate by superadmin
  regenerateCertificate(appId: number) {
    return this.http.get<boolean>(this.docGenerateURL + '/RegenerateCertificate/' + appId);
  }

  // regenerate notification letters by superadmin
  regenerateNotificationLetter(appId: number) {
    return this.http.get<boolean>(this.docGenerateURL + '/RegenerateNotificationLetter/' + appId);
  }

  getFirstTimeFee(applicationCategoryId: number, facilityTypeId: number, productTypeId: number) : Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this.feeInfoUrl}/GetFirstTimeFee/${applicationCategoryId}/${facilityTypeId}/${productTypeId}`);
  }

  getRepeatedInspectionFee(appId: number) : Observable<ApiResponse> {
    return this.http.get<ApiResponse>(`${this.feeInfoUrl}/GetRepeatedInspectionFee/${appId}`);
  }

  createTelebirrOrder(request: PaymentRequest): Observable<any> {
    return this.http.post<any>(this.telebirrUrl, request, httpOptions);
  }

  /** Reconcile Telebirr browser return (welcome redirect query) with server payment state. */
  telebirrPaymentReturn(notifyFields: Record<string, string>): Observable<{ code?: string; msg?: string; applicationId?: number; ApplicationId?: number }> {
    return this.http.post<{ code?: string; msg?: string; applicationId?: number; ApplicationId?: number }>(
      this.telebirrCallbackUrl,
      notifyFields,
      httpOptions
    );
  }

  getApplicationIdByTelebirrBill(billReference: string): Observable<{ applicationId?: number; ApplicationId?: number }> {
    return this.http.get<{ applicationId?: number; ApplicationId?: number }>(
      `${this.telebirrBillLookupUrl}/${encodeURIComponent(billReference)}`,
      httpOptions
    );
  }

  confirmPayment(confirmation: PaymentConfirmation): Observable<any> {
    return this.http.post<any>(this.confirmPaymentUrl, confirmation, httpOptions);
  }
}
